/**
 * AgentStepTimeline - Chat-agent-style step timeline for agent_step log entries
 *
 * Renders a contiguous run ("group") of `step: "agent_step"` entries from the
 * generation activity log, like Claude's agent UI: per-tool icons, human
 * labels, optional section suffix, and a chevron-expandable detail panel
 * showing tool name + input/output summaries.
 *
 * While the group is live (generation in progress and this is the latest
 * group), steps render expanded so new entries stream in view; once the run
 * completes (or a newer non-agent_step milestone follows), the group
 * collapses to a "Completed N steps" header with a chevron toggle.
 *
 * Serves the regular-user activity log only — the expert "Generation
 * internals" panel renders steps via GenerationStepsTable instead.
 */

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Globe,
  ArrowDownToLine,
  Database,
  Zap,
  PenLine,
  Wrench,
  AlertTriangle,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import type { GenerationLogEntry, AgentStepKind } from './types';

interface AgentStepTimelineProps {
  /** Contiguous run of agent_step entries (a "step group") */
  steps: GenerationLogEntry[];
  /** True while generating AND this is the latest group in the log */
  isLive: boolean;
}

const kindIcons: Record<AgentStepKind, { icon: typeof Globe; color: string }> = {
  search: { icon: Globe, color: 'text-muted-foreground' },
  fetch: { icon: ArrowDownToLine, color: 'text-muted-foreground' },
  source_pool: { icon: Database, color: 'text-muted-foreground' },
  insight: { icon: Zap, color: 'text-teal-dark' },
  write: { icon: PenLine, color: 'text-coral' },
  tool: { icon: Wrench, color: 'text-muted-foreground' },
};

function isErrorStep(step: GenerationLogEntry): boolean {
  return (
    step.level === 'warn' ||
    step.level === 'warning' ||
    step.level === 'error' ||
    step.detail?.is_error === true
  );
}

const MAX_SUMMARY_CHARS = 500;

function truncateSummary(text: string): string {
  if (text.length <= MAX_SUMMARY_CHARS) return text;
  return `${text.slice(0, MAX_SUMMARY_CHARS).trimEnd()}…`;
}

/** Single agent step row with optional expandable detail panel */
const StepRow: React.FC<{ step: GenerationLogEntry }> = ({ step }) => {
  const [open, setOpen] = useState(false);

  const failed = isErrorStep(step);
  const kindConfig = (step.kind && kindIcons[step.kind]) || kindIcons.tool;
  const Icon = failed ? AlertTriangle : kindConfig.icon;
  const iconColor = failed ? 'text-destructive' : kindConfig.color;

  const hasDetail = Boolean(
    step.tool || step.detail?.input_summary || step.detail?.output_summary,
  );
  const DetailChevron = open ? ChevronDown : ChevronRight;

  return (
    <div>
      <button
        type="button"
        onClick={() => hasDetail && setOpen(!open)}
        className={`w-full flex items-center gap-2 px-3 py-1 text-left text-xs text-muted-foreground hover:bg-muted transition-colors ${
          hasDetail ? 'cursor-pointer' : 'cursor-default'
        }`}
        aria-expanded={hasDetail ? open : undefined}
      >
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${iconColor}`} />
        <span className={`flex-1 min-w-0 truncate ${failed ? 'text-destructive' : 'text-muted-foreground'}`}>
          {step.msg}
          {step.section && (
            <span className="ml-1.5 text-muted-foreground">· {step.section}</span>
          )}
        </span>
        {hasDetail && (
          <DetailChevron className="w-3 h-3 flex-shrink-0 text-slate-300" />
        )}
      </button>

      {open && hasDetail && (
        <div className="ml-8 mr-3 mb-1.5 px-2.5 py-1.5 rounded border border-border bg-muted/70 space-y-1">
          {step.tool && (
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {step.tool}
            </div>
          )}
          {step.detail?.input_summary && (
            <div className="text-[11px] font-mono text-muted-foreground whitespace-pre-wrap break-words leading-snug">
              <span className="text-muted-foreground select-none">in&nbsp;&nbsp;</span>
              {truncateSummary(step.detail.input_summary)}
            </div>
          )}
          {step.detail?.output_summary && (
            <div
              className={`text-[11px] font-mono whitespace-pre-wrap break-words leading-snug ${
                step.detail?.is_error ? 'text-destructive' : 'text-muted-foreground'
              }`}
            >
              <span className="text-muted-foreground select-none">out&nbsp;</span>
              {truncateSummary(step.detail.output_summary)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AgentStepTimeline: React.FC<AgentStepTimelineProps> = ({ steps, isLive }) => {
  // null = user hasn't toggled; follow live state (expanded while live,
  // collapsed once the group is no longer the active run).
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? isLive;

  if (steps.length === 0) return null;

  const HeaderChevron = open ? ChevronDown : ChevronRight;
  const errorCount = steps.filter(isErrorStep).length;

  return (
    <Collapsible open={open} onOpenChange={(next) => setUserOpen(next)}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted transition-colors"
        >
          <HeaderChevron className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
          {isLive ? (
            <>
              <span className="w-2 h-2 rounded-full bg-teal animate-pulse flex-shrink-0" />
              <span className="font-medium text-muted-foreground">
                Working… {steps.length} step{steps.length === 1 ? '' : 's'}
              </span>
            </>
          ) : (
            <span className="font-medium text-muted-foreground">
              Completed {steps.length} step{steps.length === 1 ? '' : 's'}
            </span>
          )}
          {errorCount > 0 && (
            <span className="inline-flex items-center gap-1 text-destructive">
              <AlertTriangle className="w-3 h-3" />
              {errorCount}
            </span>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pb-1">
          {steps.map((step, i) => (
            <StepRow key={i} step={step} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default AgentStepTimeline;
